import { Suspense, lazy } from 'react';
import AppErrorBoundary from './components/AppErrorBoundary';
import { NavHistoryRecorder } from './lib/navHistory';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import Auth from './components/Auth';
import ResetPassword from './components/ResetPassword';
import EventSearch from './components/EventSearch';
import Profile from './components/Profile';
import PeopleSearch from './components/PeopleSearch';
import Chats from './components/Chats';
import NewMessagePage from './components/NewMessagePage';
import DmConversation from './components/DmConversation';
import GroupDetail from './components/GroupDetail';
import GroupInfoPage from './components/GroupInfoPage';
import GroupMembersPage from './components/GroupMembersPage';
import GroupLineupPage from './components/GroupLineupPage';
import Settings from './components/Settings';
import EditProfile from './components/EditProfile';
import FollowConnections from './components/FollowConnections';
import PeoplePage from './components/PeoplePage';
import ContactsPage from './components/ContactsPage';
import MyCodePage from './components/people/MyCodePage';
import WelcomeProfile from './components/onboarding/WelcomeProfile';
import Notifications from './components/Notifications';
import Find from './components/Find';
import EventLandingPage from './components/EventLandingPage';
import PicksSummaryPage from './components/PicksSummaryPage';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import QueryProvider from './lib/query/QueryProvider';
import DeepLinkHandler from './components/DeepLinkHandler';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import MapsPage from './components/MapsPage';
import SetsPage from './components/SetsPage';
import GroupsPage from './components/GroupsPage';
import { SURFACES, groupHomePath } from './config/productSurfaces';
import './components/App.css';

/** Studio, the operators' map editor: its own chunk, fetched only when /studio is opened. */
const StudioPage = lazy(() => import('./components/studio/StudioPage'));
const studio = (
  <Suspense fallback={null}>
    <StudioPage />
  </Suspense>
);
// Stage 4's motion lab (/lab/motion, /lab/loading): dev builds only, dropped from production.
const MotionLab = import.meta.env.DEV ? lazy(() => import('./components/motion/MotionLab')) : null;
const OrbsLab = import.meta.env.DEV
  ? lazy(() => import('./components/motion/MotionLab').then((m) => ({ default: m.OrbsLab })))
  : null;
const LoadingLab = import.meta.env.DEV
  ? lazy(() => import('./components/motion/MotionLab').then((m) => ({ default: m.LoadingLab })))
  : null;
// Stage 5's Profile + Discover options (/lab/profile): dev only.
const ProfileLab = import.meta.env.DEV ? lazy(() => import('./components/lab/ProfileLab')) : null;
// Stage 2 picks (/lab/picks, 2026-11-04): dev only.
const StudioForEvent = lazy(() =>
  import('./components/studio/StudioPage').then((m) => ({ default: m.StudioForEvent })),
);
const studioForEvent = (
  <Suspense fallback={null}>
    <StudioForEvent />
  </Suspense>
);

/** With chat hidden, a group's home is its info page — never a 404 or a dead thread. */
function GroupHome() {
  const { groupId } = useParams();
  const location = useLocation();
  // Keep the query and state: a shared link's ?invite= code and the just-created flag ride through this hop.
  return (
    <Navigate to={`${groupHomePath(groupId)}${location.search}`} state={location.state} replace />
  );
}

/** Old-style invite links (/groups/:id/invite/:code) land on the group page with the code, which joins through join_group. */
function LegacyGroupInvite() {
  const { groupId, inviteCode } = useParams();
  return (
    <Navigate to={`/groups/${groupId}?invite=${encodeURIComponent(inviteCode || '')}`} replace />
  );
}

/** A hidden surface redirects; it does not disappear from under a user mid-flow. */
const toGroups = <Navigate to="/groups" replace />;

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <QueryProvider>
          <div className="App min-h-screen transition-colors">
            <BrowserRouter>
              <DeepLinkHandler />
              <NavHistoryRecorder />
              <AppErrorBoundary>
                <Routes>
                  <Route element={<Layout />}>
                    <Route path="/" element={null} />{' '}
                    {/* Events is kept mounted by Layout (it hides it on other tabs) */}
                    <Route path="/events/search" element={<EventSearch />} />
                    <Route path="/people" element={<PeoplePage />} />
                    <Route path="/people/contacts" element={<ContactsPage />} />
                    <Route path="/code" element={<MyCodePage />} />{' '}
                    {/* inside Layout: it needs the signed-in user (CurrentUserProfileProvider); full screen over the header and dock */}
                    <Route path="/people/search" element={<PeopleSearch />} />
                    <Route
                      path="/groups"
                      element={SURFACES.chats ? <Navigate to="/chats" replace /> : <GroupsPage />}
                    />
                    <Route path="/chats" element={SURFACES.chats ? <Chats /> : toGroups} />
                    <Route
                      path="/chats/new"
                      element={SURFACES.chats ? <NewMessagePage /> : toGroups}
                    />
                    <Route path="/find" element={<Find />} />
                    <Route path="/map" element={<MapsPage />} />
                    <Route path="/map/:eventId" element={<MapsPage />} />
                    <Route path="/sets" element={<SetsPage />} />
                    <Route path="/sets/:eventId" element={<SetsPage />} />
                    <Route
                      path="/chats/dm/:conversationId"
                      element={SURFACES.chats ? <DmConversation /> : toGroups}
                    />
                    <Route
                      path="/groups/:groupId"
                      element={SURFACES.chats ? <GroupDetail /> : <GroupHome />}
                    />
                    <Route path="/groups/:groupId/info" element={<GroupInfoPage />} />
                    <Route path="/groups/:groupId/members" element={<GroupMembersPage />} />
                    <Route path="/groups/:groupId/lineup" element={<GroupLineupPage />} />
                    <Route
                      path="/groups/:groupId/invite/:inviteCode"
                      element={<LegacyGroupInvite />}
                    />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/profile/edit" element={<EditProfile />} />
                    <Route path="/profile/followers" element={<PeoplePage tab="followers" />} />
                    <Route path="/profile/following" element={<PeoplePage tab="following" />} />
                    <Route path="/profile/discover" element={<Navigate to="/people" replace />} />
                    <Route path="/welcome/profile" element={<WelcomeProfile />} />
                    <Route
                      path="/profile/:userId/followers"
                      element={<FollowConnections tab="followers" />}
                    />
                    <Route
                      path="/profile/:userId/following"
                      element={<FollowConnections tab="following" />}
                    />
                    <Route path="/profile/:userId" element={<Profile />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/events/:eventId" element={<EventLandingPage />} />
                    <Route path="/events/:eventId/picks" element={<PicksSummaryPage />} />
                    <Route path="/events/:eventId/picks/:dayIndex" element={<PicksSummaryPage />} />
                    {LoadingLab && (
                      <Route
                        path="/lab/loading"
                        element={
                          <Suspense fallback={null}>
                            <LoadingLab />
                          </Suspense>
                        }
                      />
                    )}
                    {ProfileLab && (
                      <Route
                        path="/lab/profile"
                        element={
                          <Suspense fallback={null}>
                            <ProfileLab />
                          </Suspense>
                        }
                      />
                    )}
                  </Route>
                  {MotionLab && (
                    <Route
                      path="/lab/motion"
                      element={
                        <Suspense fallback={null}>
                          <MotionLab />
                        </Suspense>
                      }
                    />
                  )}
                  {OrbsLab && (
                    <Route
                      path="/lab/orbs"
                      element={
                        <Suspense fallback={null}>
                          <OrbsLab />
                        </Suspense>
                      }
                    />
                  )}
                  <Route path="/studio" element={studio} />
                  <Route path="/studio/:editionId" element={studio} />
                  <Route path="/map/:eventId/studio" element={studioForEvent} />
                  <Route path="/login" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  {/* A short-link shape nothing generates any more (it never carried the group); send it somewhere useful. */}
                  <Route path="/g/:inviteCode" element={<Navigate to="/groups" replace />} />
                  {/* An address the app does not know goes home, never to a blank page. */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AppErrorBoundary>
            </BrowserRouter>
          </div>
        </QueryProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
